'use client';

import type Lenis from 'lenis';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { SCROLL_LOCK_WILL_LOCK_EVENT } from '@/app/components/overlays/utils/scrollLock';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { shouldUseAndroidProductScrollFallback, SPARTAN_PRODUCT_ANDROID_LINE_CLASS } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/browser';
import { isSpartanProductHeroReady, SPARTAN_PRODUCT_HERO_READY_EVENT, SPARTAN_PRODUCT_LAYOUT_DID_CHANGE_EVENT, SPARTAN_PRODUCT_LAYOUT_WILL_CHANGE_EVENT } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/events';
import { observeSpartanProductParallaxFallback } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/parallax-fallback';
import { getSpartanProductScrollPathScope } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/path-scope';
import { observeSpartanProductReveals } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/reveal-observer';
import { getSpartanProductLenis, notifySpartanProductScroll, setSpartanProductLenis } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/store';
import { SPARTAN_REVEAL_READY_CLASS } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
const SPARTAN_PRODUCT_LENIS_OPTIONS = {
  orientation: 'vertical',
  gestureOrientation: 'vertical',
  smoothWheel: true,
  syncTouch: false,
  overscroll: false,
  wheelMultiplier: 1,
  lerp: 0.14,
  autoRaf: true
} as const;
const SPARTAN_PARALLAX_PAUSED_CLASS = 'spartan-parallax-paused';
const SPARTAN_PARALLAX_SELECTOR = '[data-spartan-parallax]';
const SPARTAN_PARALLAX_FROZEN_TRANSFORM = '--spartan-parallax-frozen-transform';
const SPARTAN_LENIS_HERO_SETTLE_DELAY_MS = 1400;
const SPARTAN_LENIS_INPUT_IDLE_DELAY_MS = 150;
export const SpartanProductScroll = () => {
  const pathname = usePathname() ?? '';
  const pathScope = getSpartanProductScrollPathScope(pathname);
  const lenisRef = useRef<Lenis | null>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', false) ?? false;
  useEffect(() => {
    document.documentElement.classList.remove(SPARTAN_REVEAL_READY_CLASS);
    // Full product navigations (e.g. RS → Ultimate) remount Lenis; reset scroll
    // so lineup / model-switch CTAs land at the top of the next page.
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
    if (prefersReducedMotion) return;
    const shouldUseNativeScroll = shouldUseAndroidProductScrollFallback(navigator.userAgent);
    document.documentElement.classList.toggle(SPARTAN_PRODUCT_ANDROID_LINE_CLASS, shouldUseNativeScroll);
    const stopObservingReveals = observeSpartanProductReveals();
    const stopObservingParallax = shouldUseNativeScroll ? () => undefined : observeSpartanProductParallaxFallback();
    let isDisposed = false;
    let isScrollLocked: boolean | null = null;
    let parallaxResumeFrameId: number | null = null;
    let scrollLockObserver: MutationObserver | null = null;
    let unsubscribeScroll: (() => void) | undefined;
    let hasStartedLenis = false;
    let hasUserScrollIntent = false;
    let isStartingLenis = false;
    let lastScrollActivityAt = Number.NEGATIVE_INFINITY;
    let lenisIdleTimeoutId: number | null = null;
    let lenisStartTimeoutId: number | null = null;
    const cancelParallaxResume = () => {
      if (parallaxResumeFrameId === null) return;
      window.cancelAnimationFrame(parallaxResumeFrameId);
      parallaxResumeFrameId = null;
    };
    const pauseParallax = () => {
      cancelParallaxResume();
      if (document.documentElement.classList.contains(SPARTAN_PARALLAX_PAUSED_CLASS)) {
        return;
      }
      document.querySelectorAll<HTMLElement>(SPARTAN_PARALLAX_SELECTOR).forEach(element => {
        element.style.setProperty(SPARTAN_PARALLAX_FROZEN_TRANSFORM, window.getComputedStyle(element).transform);
      });
      document.documentElement.classList.add(SPARTAN_PARALLAX_PAUSED_CLASS);
    };
    const clearFrozenParallaxTransforms = () => {
      document.querySelectorAll<HTMLElement>(SPARTAN_PARALLAX_SELECTOR).forEach(element_0 => {
        element_0.style.removeProperty(SPARTAN_PARALLAX_FROZEN_TRANSFORM);
      });
    };
    const resumeParallaxAfterLayoutSettles = () => {
      if (!document.documentElement.classList.contains(SPARTAN_PARALLAX_PAUSED_CLASS)) {
        return;
      }
      cancelParallaxResume();
      parallaxResumeFrameId = window.requestAnimationFrame(() => {
        parallaxResumeFrameId = window.requestAnimationFrame(() => {
          parallaxResumeFrameId = null;
          document.documentElement.classList.remove(SPARTAN_PARALLAX_PAUSED_CLASS);
          clearFrozenParallaxTransforms();
          notifySpartanProductScroll();
        });
      });
    };
    const syncScrollLock = () => {
      const lenis = lenisRef.current;
      const nextIsScrollLocked = document.documentElement.classList.contains('scroll-locked');
      if (nextIsScrollLocked === isScrollLocked) return;
      isScrollLocked = nextIsScrollLocked;
      if (nextIsScrollLocked) {
        pauseParallax();
        lenis?.stop();
        return;
      }
      lenis?.start();
      resumeParallaxAfterLayoutSettles();
    };
    window.addEventListener(SCROLL_LOCK_WILL_LOCK_EVENT, pauseParallax);
    window.addEventListener(SPARTAN_PRODUCT_LAYOUT_WILL_CHANGE_EVENT, pauseParallax);
    window.addEventListener(SPARTAN_PRODUCT_LAYOUT_DID_CHANGE_EVENT, resumeParallaxAfterLayoutSettles);
    const clearLenisIdleTimeout = () => {
      if (lenisIdleTimeoutId === null) return;
      window.clearTimeout(lenisIdleTimeoutId);
      lenisIdleTimeoutId = null;
    };
    const clearLenisStartTimeout = () => {
      if (lenisStartTimeoutId === null) return;
      window.clearTimeout(lenisStartTimeoutId);
      lenisStartTimeoutId = null;
    };
    const removeStartListeners = () => {
      window.removeEventListener(SPARTAN_PRODUCT_HERO_READY_EVENT, scheduleLenisAfterHeroSettles);
      window.removeEventListener('wheel', handleScrollIntent);
      window.removeEventListener('touchstart', handleScrollIntent);
      window.removeEventListener('keydown', handleScrollIntent);
      window.removeEventListener('scroll', handleNativeScroll);
    };
    function startLenis() {
      if (hasStartedLenis || isStartingLenis || isDisposed) return;
      isStartingLenis = true;
      clearLenisIdleTimeout();
      clearLenisStartTimeout();
      void import('lenis').then(({
        default: LenisConstructor
      }) => {
        isStartingLenis = false;
        if (isDisposed) return;
        const timeSinceScrollActivity = window.performance.now() - lastScrollActivityAt;
        if (hasUserScrollIntent && timeSinceScrollActivity < SPARTAN_LENIS_INPUT_IDLE_DELAY_MS) {
          scheduleLenisAfterInputIdle();
          return;
        }
        hasStartedLenis = true;
        removeStartListeners();
        const currentScrollY = window.scrollY;
        const lenis_0 = new LenisConstructor(SPARTAN_PRODUCT_LENIS_OPTIONS);
        lenis_0.scrollTo(currentScrollY, {
          force: true,
          immediate: true
        });
        lenisRef.current = lenis_0;
        setSpartanProductLenis(lenis_0);
        unsubscribeScroll = lenis_0.on('scroll', notifySpartanProductScroll);
        syncScrollLock();
        scrollLockObserver = new MutationObserver(syncScrollLock);
        scrollLockObserver.observe(document.documentElement, {
          attributeFilter: ['class'],
          attributes: true
        });
      });
    }
    function scheduleLenisAfterInputIdle() {
      if (hasStartedLenis || isDisposed) return;
      clearLenisIdleTimeout();
      lenisIdleTimeoutId = window.setTimeout(() => {
        lenisIdleTimeoutId = null;
        startLenis();
      }, SPARTAN_LENIS_INPUT_IDLE_DELAY_MS);
    }
    function handleScrollIntent() {
      if (hasStartedLenis || isDisposed) return;
      hasUserScrollIntent = true;
      lastScrollActivityAt = window.performance.now();
      clearLenisStartTimeout();
      scheduleLenisAfterInputIdle();
    }
    function handleNativeScroll() {
      if (!hasUserScrollIntent || hasStartedLenis || isDisposed) return;
      lastScrollActivityAt = window.performance.now();
      scheduleLenisAfterInputIdle();
    }
    function scheduleLenisAfterHeroSettles() {
      if (hasStartedLenis || hasUserScrollIntent || lenisStartTimeoutId !== null) {
        return;
      }
      lenisStartTimeoutId = window.setTimeout(() => {
        lenisStartTimeoutId = null;
        startLenis();
      }, SPARTAN_LENIS_HERO_SETTLE_DELAY_MS);
    }
    if (shouldUseNativeScroll) {
      window.addEventListener('scroll', notifySpartanProductScroll, {
        passive: true
      });
      syncScrollLock();
      scrollLockObserver = new MutationObserver(syncScrollLock);
      scrollLockObserver.observe(document.documentElement, {
        attributeFilter: ['class'],
        attributes: true
      });
    } else {
      window.addEventListener(SPARTAN_PRODUCT_HERO_READY_EVENT, scheduleLenisAfterHeroSettles);
      window.addEventListener('wheel', handleScrollIntent, {
        passive: true
      });
      window.addEventListener('touchstart', handleScrollIntent, {
        passive: true
      });
      window.addEventListener('keydown', handleScrollIntent);
      window.addEventListener('scroll', handleNativeScroll, {
        passive: true
      });
      if (isSpartanProductHeroReady()) scheduleLenisAfterHeroSettles();
    }
    return () => {
      isDisposed = true;
      clearLenisIdleTimeout();
      clearLenisStartTimeout();
      stopObservingReveals();
      stopObservingParallax();
      cancelParallaxResume();
      document.documentElement.classList.remove(SPARTAN_PARALLAX_PAUSED_CLASS);
      document.documentElement.classList.remove(SPARTAN_PRODUCT_ANDROID_LINE_CLASS);
      clearFrozenParallaxTransforms();
      removeStartListeners();
      window.removeEventListener('scroll', notifySpartanProductScroll);
      window.removeEventListener(SCROLL_LOCK_WILL_LOCK_EVENT, pauseParallax);
      window.removeEventListener(SPARTAN_PRODUCT_LAYOUT_WILL_CHANGE_EVENT, pauseParallax);
      window.removeEventListener(SPARTAN_PRODUCT_LAYOUT_DID_CHANGE_EVENT, resumeParallaxAfterLayoutSettles);
      scrollLockObserver?.disconnect();
      unsubscribeScroll?.();
      const lenis_1 = lenisRef.current;
      if (lenis_1 && getSpartanProductLenis() === lenis_1) {
        setSpartanProductLenis(undefined);
      }
      lenis_1?.destroy();
      lenisRef.current = null;
    };
  }, [pathScope, prefersReducedMotion]);
  return null;
};